import { useState } from 'react';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import MemberPicker from './MemberPicker';
import PlanCard from './PlanCard';
import Segmented from './Segmented';
import {
  categoryFor,
  formatMoney,
  householdOptions,
  intervalSuffix,
  isUpgrade,
  needsMemberPicking,
  priceFor,
  renewalDate,
  seatLimit,
  visiblePlans,
  HOUSEHOLD_LABELS,
  LABEL,
} from './helper';

const Line = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 px-3.5 py-2.5">
    <span className="text-xs font-medium text-secondary-500">{label}</span>
    <span className="text-xs font-bold text-secondary-900">{value}</span>
  </div>
);

// Tier and household size can both move; the billing period can't — switching
// monthly to annual moves the billing anchor, which is a new subscription
// rather than a plan change.
const ChangePlan = ({ enrollment, plans = [], people = [], busy = false, onConfirm, onBack }) => {
  const { plan_name, household_type, interval, price_dollars, members = [] } = enrollment || {};
  const [household, setHousehold] = useState(household_type);
  const [picked, setPicked] = useState(null);
  const [picking, setPicking] = useState(null);

  const renewal = renewalDate(enrollment);
  const suffix = intervalSuffix(interval);
  const subscriberId = people.find(({ is_subscriber }) => is_subscriber)?.id;

  // Every household the practice sells at the period they're already billed on.
  // Whether this patient has the family to fill one is the picker's answer to
  // give, exactly as it is when joining — hiding the option instead would leave
  // a single member with no idea couple and family plans exist.
  const households = householdOptions(plans).filter(
    ({ key }) => visiblePlans(plans, key, interval).length > 0
  );
  const activeHousehold = households.some(({ key }) => key === household) ? household : household_type;

  const confirm = (plan, memberIds) => setPicked({ plan, memberIds });

  // Who the plan covers today, trimmed to what the new plan can seat — the
  // patient decides which of them keeps a place.
  const choosePlan = (plan) => {
    if (!needsMemberPicking(activeHousehold)) return confirm(plan, [subscriberId].filter(Boolean));

    const limit = seatLimit(plan, activeHousehold);
    const keeping = members.map(({ id }) => id).filter((id) => id !== subscriberId);

    setPicking({
      plan,
      selectedIds: [subscriberId, ...keeping.slice(0, Math.max(limit - 1, 0))].filter(Boolean),
    });
  };

  const toggleMember = (id) =>
    setPicking((current) => ({
      ...current,
      selectedIds: current.selectedIds.includes(id)
        ? current.selectedIds.filter((selected) => selected !== id)
        : [...current.selectedIds, id],
    }));

  if (picked) {
    const { plan, memberIds } = picked;
    const price = priceFor(plan, activeHousehold, interval);
    const up = isUpgrade(price, price_dollars);
    const covered = people.filter(({ id }) => memberIds.includes(id)).map(({ name }) => name);

    return (
      <div className="animate-slide-up rounded-2xl border border-secondary-200 bg-white p-6 sm:p-8">
        <h3 className="text-lg font-bold text-secondary-900">
          {up ? `Upgrade to ${plan.name}?` : `Switch to ${plan.name}?`}
        </h3>
        <p className="mt-1 text-xs leading-relaxed text-secondary-500">
          {up
            ? 'Your card on file is charged today for the rest of this billing period.'
            : 'Nothing is charged today, and you keep what you have already paid for.'}
        </p>

        <div className="mt-4 divide-y divide-secondary-100 rounded-xl border border-secondary-200 bg-secondary-50/60">
          <Line
            label="New plan"
            value={`${plan.name} · ${HOUSEHOLD_LABELS[activeHousehold] || activeHousehold}`}
          />
          <Line label="New price" value={`${formatMoney(price)} ${suffix}`} />
          {covered.length > 0 && <Line label="Covers" value={covered.join(', ')} />}
          <Line label="Today" value={up ? 'The difference, prorated' : 'Nothing'} />
          <Line
            label="Takes effect"
            value={up ? 'Straight away' : renewal ? `${renewal} (next renewal)` : 'Next renewal'}
          />
        </div>

        <p className="mt-2.5 text-xs leading-relaxed text-secondary-500">
          {up ? (
            <>
              You move to <strong>{plan.name}</strong> now — Stripe bills the difference between your{' '}
              {plan_name} plan and this one for the days left in this period, then{' '}
              <strong>{formatMoney(price)}</strong> {suffix} from {renewal}.
            </>
          ) : (
            <>
              Everyone on your <strong>{plan_name}</strong> plan stays covered until{' '}
              <strong>{renewal}</strong>, because you have already paid for it. From that date you
              are on <strong>{plan.name}</strong> at <strong>{formatMoney(price)}</strong> {suffix}.
              You can switch back any time before then.
            </>
          )}
        </p>

        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => onConfirm(categoryFor(plan, activeHousehold), memberIds)}
            disabled={busy}
            className="btn h-10 min-h-10 flex-1 gap-2 rounded-xl border-0 bg-secondary-900 text-xs font-semibold text-white hover:bg-secondary-800 disabled:opacity-50"
          >
            <ShieldCheck size={14} strokeWidth={2.5} />
            {busy ? 'Updating…' : up ? 'Yes — upgrade and pay the difference' : 'Yes — switch at renewal'}
          </button>
          <button
            type="button"
            onClick={() => setPicked(null)}
            disabled={busy}
            className="btn h-10 min-h-10 gap-2 rounded-xl border border-secondary-200 bg-white px-4 text-xs font-semibold text-secondary-700 hover:bg-secondary-50 disabled:opacity-50"
          >
            Never mind
          </button>
        </div>
      </div>
    );
  }

  if (picking) {
    const { plan, selectedIds } = picking;

    return (
      <MemberPicker
        plan={plan}
        household={activeHousehold}
        people={people}
        limit={seatLimit(plan, activeHousehold)}
        selectedIds={selectedIds}
        busy={busy}
        continueLabel="Review the change"
        onToggle={toggleMember}
        onBack={() => setPicking(null)}
        onContinue={() => confirm(plan, selectedIds)}
      />
    );
  }

  return (
    <div className="animate-slide-up">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <p className={LABEL}>Change plan</p>
          <p className="mt-1 text-base font-bold text-secondary-900">
            You're on {plan_name} · {HOUSEHOLD_LABELS[household_type] || household_type} ·{' '}
            {formatMoney(price_dollars)} {suffix}
          </p>
          <p className="mt-0.5 text-xs text-secondary-400">
            Upgrades start today. Anything cheaper starts at your next renewal.
          </p>
        </div>

        <button
          type="button"
          onClick={onBack}
          className="btn h-9 min-h-9 gap-2 rounded-xl border border-secondary-200 bg-white px-3 text-xs font-semibold text-secondary-700 hover:bg-secondary-50"
        >
          <ArrowLeft size={14} />
          Back
        </button>
      </div>

      {households.length > 1 && (
        <div className="mb-5">
          <p className={LABEL}>Who it covers</p>
          <div className="mt-2">
            <Segmented options={households} value={activeHousehold} onChange={setHousehold} fill />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 pt-1 sm:grid-cols-2 lg:grid-cols-3">
        {visiblePlans(plans, activeHousehold, interval).map((plan) => {
          const current = plan.name === plan_name && activeHousehold === household_type;
          const up = isUpgrade(priceFor(plan, activeHousehold, interval), price_dollars);

          return (
            <PlanCard
              key={plan.id}
              plan={plan}
              household={activeHousehold}
              interval={interval}
              disabled={busy}
              current={current}
              cta={up ? `Upgrade to ${plan.name}` : `Switch to ${plan.name}`}
              // Anything cheaper starts at the renewal, so the date belongs next
              // to the choice — not only in the confirmation after it.
              note={up ? 'Starts today' : renewal ? `Starts ${renewal}` : 'Starts at your next renewal'}
              onChoose={choosePlan}
            />
          );
        })}
      </div>
    </div>
  );
};

export default ChangePlan;
